import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

// Application Status Checker Component
const ApplicationStatusChecker = () => {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState<null | "not_found" | { status: string; restaurantName: string; appliedAt: string; approvedAt?: string }>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleCheck = () => {
    if (!email.trim()) return;
    setLoading(true);
    setSearched(true);

    setTimeout(() => {
      const pending = JSON.parse(localStorage.getItem("pendingRestaurants") || "[]");
      const approved = JSON.parse(localStorage.getItem("approvedRestaurants") || "[]");
      const rejected = JSON.parse(localStorage.getItem("rejectedRestaurants") || "[]");

      const allApplications = [
        ...pending.map((r: { email: string; restaurantName: string; appliedAt: string }) => ({ ...r, status: "pending" })),
        ...approved.map((r: { email: string; restaurantName: string; appliedAt: string; approvedAt?: string }) => ({ ...r, status: "approved" })),
        ...rejected.map((r: { email: string; restaurantName: string; appliedAt: string }) => ({ ...r, status: "rejected" })),
      ];

      const found = allApplications.find(
        (r) => r.email?.toLowerCase().trim() === email.toLowerCase().trim()
      );

      if (found) {
        setResult({
          status: found.status,
          restaurantName: found.restaurantName,
          appliedAt: found.appliedAt,
          approvedAt: found.approvedAt,
        });
      } else {
        setResult("not_found");
      }
      setLoading(false);
    }, 800);
  };

  const statusConfig = {
    pending: {
      icon: "ri-time-line",
      color: "text-amber-600",
      bg: "bg-amber-50",
      border: "border-amber-200",
      label: "İnceleme Bekliyor",
      desc: "Başvurunuz editörlerimiz tarafından inceleniyor. Bu süreç genellikle 2-5 iş günü sürmektedir.",
    },
    approved: {
      icon: "ri-checkbox-circle-line",
      color: "text-emerald-600",
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      label: "Onaylandı & Yayında",
      desc: "Tebrikler! Restoranınız onaylandı ve sitemizde yayınlandı.",
    },
    rejected: {
      icon: "ri-close-circle-line",
      color: "text-rose-600",
      bg: "bg-rose-50",
      border: "border-rose-200",
      label: "Reddedildi",
      desc: "Başvurunuz bu sefer kabul edilmedi. Bilgilerinizi güncelleyerek tekrar başvurabilirsiniz.",
    },
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-6 md:p-8">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 flex items-center justify-center bg-stone-100 rounded-xl flex-shrink-0">
          <i className="ri-search-eye-line text-stone-600 text-lg"></i>
        </div>
        <div>
          <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Başvuru Durumu Sorgula
          </h3>
          <p className="text-stone-400 text-xs">Başvurunuzun güncel durumunu e-posta adresinizle öğrenin</p>
        </div>
      </div>

      <div className="mt-5 flex gap-2">
        <div className="flex-1 flex items-center bg-stone-50 border border-stone-200 rounded-lg px-3 gap-2 focus-within:border-amber-400 transition-colors">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-mail-line text-stone-400 text-sm"></i>
          </div>
          <input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setResult(null); setSearched(false); }}
            onKeyDown={(e) => e.key === "Enter" && handleCheck()}
            placeholder="Başvuruda kullandığınız e-posta..."
            className="flex-1 bg-transparent text-sm text-stone-700 outline-none py-2.5"
          />
        </div>
        <button
          type="button"
          onClick={handleCheck}
          disabled={loading || !email.trim()}
          className="px-5 py-2.5 bg-stone-900 text-white rounded-lg text-sm font-medium hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          {loading ? <i className="ri-loader-4-line animate-spin"></i> : "Sorgula"}
        </button>
      </div>

      {searched && !loading && result === "not_found" && (
        <div className="mt-4 flex items-start gap-3 p-4 bg-stone-50 rounded-xl border border-stone-200">
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
            <i className="ri-question-line text-stone-400 text-lg"></i>
          </div>
          <div>
            <p className="text-sm font-semibold text-stone-700">Başvuru Bulunamadı</p>
            <p className="text-xs text-stone-400 mt-0.5 leading-relaxed">
              Bu e-posta adresiyle kayıtlı bir başvuru bulunamadı. E-posta adresinizi kontrol edip tekrar deneyin.
            </p>
          </div>
        </div>
      )}

      {searched && !loading && result && result !== "not_found" && (() => {
        const cfg = statusConfig[result.status as keyof typeof statusConfig];
        return (
          <div className={`mt-4 p-4 rounded-xl border ${cfg.bg} ${cfg.border}`}>
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 flex items-center justify-center rounded-full ${cfg.bg} flex-shrink-0`}>
                <i className={`${cfg.icon} ${cfg.color} text-xl`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-sm font-bold ${cfg.color}`}>{cfg.label}</p>
                  <span className="text-xs text-stone-400">·</span>
                  <p className="text-xs text-stone-500 font-medium truncate">{result.restaurantName}</p>
                </div>
                <p className="text-xs text-stone-500 mt-1 leading-relaxed">{cfg.desc}</p>
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  <div className="flex items-center gap-1">
                    <i className="ri-calendar-line text-stone-400 text-xs"></i>
                    <span className="text-xs text-stone-400">
                      Başvuru: {new Date(result.appliedAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                    </span>
                  </div>
                  {result.approvedAt && (
                    <div className="flex items-center gap-1">
                      <i className="ri-check-line text-emerald-500 text-xs"></i>
                      <span className="text-xs text-stone-400">
                        Onay: {new Date(result.approvedAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const cuisineOptions = [
  "Anadolu Mutfağı", "Ege Mutfağı", "Karadeniz Mutfağı", "Güneydoğu Mutfağı",
  "Akdeniz Mutfağı", "İstanbul Mutfağı", "Gaziantep Mutfağı", "Mardin Mutfağı",
  "Modern Türk", "Fine Dining", "Kahvaltı & Brunch", "Deniz Ürünleri",
  "Vejetaryen & Vegan", "Dünya Mutfağı", "Diğer"
];

const priceOptions = ["₺", "₺₺", "₺₺₺", "₺₺₺₺"];

const cities = [
  "İstanbul", "Ankara", "İzmir", "Antalya", "Bursa", "Gaziantep", "Konya",
  "Trabzon", "Mardin", "Cappadocia / Nevşehir", "Bodrum", "Alaçatı", "Fethiye",
  "Kapadokya", "Van", "Diyarbakır", "Edirne", "Kars", "Diğer"
];

// City center coordinates for map initialization
const cityCoords: Record<string, { lat: number; lng: number }> = {
  "İstanbul": { lat: 41.0082, lng: 28.9784 },
  "Ankara": { lat: 39.9334, lng: 32.8597 },
  "İzmir": { lat: 38.4192, lng: 27.1287 },
  "Antalya": { lat: 36.8969, lng: 30.7133 },
  "Bursa": { lat: 40.1885, lng: 29.0610 },
  "Gaziantep": { lat: 37.0662, lng: 37.3833 },
  "Konya": { lat: 37.8746, lng: 32.4932 },
  "Trabzon": { lat: 41.0015, lng: 39.7178 },
  "Mardin": { lat: 37.3126, lng: 40.7351 },
  "Bodrum": { lat: 37.0344, lng: 27.4305 },
  "Alaçatı": { lat: 38.2833, lng: 26.3769 },
  "Fethiye": { lat: 36.6220, lng: 29.1167 },
  "Van": { lat: 38.4891, lng: 43.4089 },
  "Diyarbakır": { lat: 37.9144, lng: 40.2306 },
  "Edirne": { lat: 41.6818, lng: 26.5623 },
  "Kars": { lat: 40.6013, lng: 43.0975 },
};

interface FormData {
  restaurantName: string;
  ownerName: string;
  email: string;
  phone: string;
  city: string;
  district: string;
  address: string;
  cuisine: string;
  priceRange: string;
  shortDesc: string;
  description: string;
  mustTry: string;
  website: string;
  instagram: string;
  openingHours: string;
  lat: string;
  lng: string;
}

const MAX_PHOTOS = 5;
const MAX_FILE_SIZE_MB = 5;

// Map Location Picker Component
const MapLocationPicker = ({
  lat, lng, city, onLocationSelect
}: {
  lat: string;
  lng: string;
  city: string;
  onLocationSelect: (lat: string, lng: string, address: string) => void;
}) => {
  const [mapSearch, setMapSearch] = useState("");
  const [mapCenter, setMapCenter] = useState(() => {
    if (lat && lng) return { lat: parseFloat(lat), lng: parseFloat(lng) };
    return cityCoords[city] || { lat: 39.9334, lng: 32.8597 };
  });
  const [pinLat, setPinLat] = useState(lat ? parseFloat(lat) : 0);
  const [pinLng, setPinLng] = useState(lng ? parseFloat(lng) : 0);
  const [hasPinned, setHasPinned] = useState(!!(lat && lng));
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  useEffect(() => {
    if (city && cityCoords[city] && !lat) {
      setMapCenter(cityCoords[city]);
    }
  }, [city, lat]);

  const getMapEmbedUrl = () => {
    if (hasPinned && pinLat && pinLng) {
      return `https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d2000!2d${pinLng}!3d${pinLat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1str!2str!4v1700000099`;
    }
    return `https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d8000!2d${mapCenter.lng}!3d${mapCenter.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1str!2str!4v1700000098`;
  };

  const handleSearch = async () => {
    if (!mapSearch.trim()) return;
    setSearchLoading(true);
    setSearchError("");
    try {
      const query = encodeURIComponent(mapSearch + (city ? `, ${city}, Türkiye` : ", Türkiye"));
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1&accept-language=tr`);
      const data = await res.json();
      if (data && data.length > 0) {
        const result = data[0];
        const newLat = parseFloat(result.lat);
        const newLng = parseFloat(result.lon);
        setMapCenter({ lat: newLat, lng: newLng });
        setPinLat(newLat);
        setPinLng(newLng);
        setHasPinned(true);
        onLocationSelect(newLat.toFixed(6), newLng.toFixed(6), result.display_name);
      } else {
        setSearchError("Konum bulunamadı. Farklı bir arama deneyin.");
      }
    } catch {
      setSearchError("Arama sırasında hata oluştu.");
    }
    setSearchLoading(false);
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setSearchError("Tarayıcınız konum özelliğini desteklemiyor.");
      return;
    }
    setSearchLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newLat = pos.coords.latitude;
        const newLng = pos.coords.longitude;
        setMapCenter({ lat: newLat, lng: newLng });
        setPinLat(newLat);
        setPinLng(newLng);
        setHasPinned(true);
        onLocationSelect(newLat.toFixed(6), newLng.toFixed(6), "");
        setSearchLoading(false);
      },
      () => {
        setSearchError("Konum alınamadı. Lütfen tarayıcı izinlerini kontrol edin.");
        setSearchLoading(false);
      }
    );
  };

  const handleManualCoords = () => {
    const latVal = prompt("Enlem (Latitude) girin:", pinLat ? pinLat.toString() : "");
    const lngVal = prompt("Boylam (Longitude) girin:", pinLng ? pinLng.toString() : "");
    if (latVal && lngVal) {
      const newLat = parseFloat(latVal);
      const newLng = parseFloat(lngVal);
      if (!isNaN(newLat) && !isNaN(newLng)) {
        setMapCenter({ lat: newLat, lng: newLng });
        setPinLat(newLat);
        setPinLng(newLng);
        setHasPinned(true);
        onLocationSelect(newLat.toFixed(6), newLng.toFixed(6), "");
      }
    }
  };

  return (
    <div className="space-y-3">
      {/* Search Bar */}
      <div className="flex gap-2">
        <div className="flex-1 flex items-center bg-stone-50 border border-stone-200 rounded-lg px-3 gap-2 focus-within:border-amber-400 transition-colors">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-search-line text-stone-400 text-sm"></i>
          </div>
          <input
            type="text"
            value={mapSearch}
            onChange={(e) => setMapSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Restoran adı veya adres ara..."
            className="flex-1 bg-transparent text-sm text-stone-700 outline-none py-2.5"
          />
        </div>
        <button
          type="button"
          onClick={handleSearch}
          disabled={searchLoading}
          className="px-4 py-2.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          {searchLoading ? <i className="ri-loader-4-line animate-spin"></i> : "Ara"}
        </button>
      </div>

      {searchError && (
        <p className="text-rose-500 text-xs">{searchError}</p>
      )}

      {/* Quick Actions */}
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={handleUseCurrentLocation}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-stone-100 text-stone-600 rounded-full hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-focus-3-line text-amber-500"></i> Mevcut Konumumu Kullan
        </button>
        <button
          type="button"
          onClick={handleManualCoords}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-stone-100 text-stone-600 rounded-full hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-edit-line text-stone-500"></i> Koordinat Gir
        </button>
      </div>

      {/* Map Embed */}
      <div className="relative rounded-xl overflow-hidden border border-stone-200" style={{ height: 300 }}>
        <iframe
          key={`${mapCenter.lat}-${mapCenter.lng}-${hasPinned}`}
          title="Konum Haritası"
          src={getMapEmbedUrl()}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
        {/* Overlay hint */}
        {!hasPinned && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm whitespace-nowrap">
            <i className="ri-map-pin-line mr-1"></i>Yukarıdan adres arayın veya konumunuzu kullanın
          </div>
        )}
      </div>

      {/* Pinned Location Info */}
      {hasPinned && pinLat && pinLng && (
        <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0">
            <i className="ri-map-pin-2-fill text-emerald-600 text-sm"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-emerald-800">Konum İşaretlendi</p>
            <p className="text-xs text-emerald-600 mt-0.5">
              {pinLat.toFixed(5)}, {pinLng.toFixed(5)}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setHasPinned(false);
              setPinLat(0);
              setPinLng(0);
              onLocationSelect("", "", "");
            }}
            className="text-xs text-emerald-600 hover:text-emerald-800 cursor-pointer whitespace-nowrap"
          >
            Temizle
          </button>
        </div>
      )}

      <p className="text-xs text-stone-400">
        <i className="ri-information-line mr-1"></i>
        Harita üzerinde konumunuzu arayın. Koordinatlar otomatik kaydedilir ve harita görünümünde restoranınız doğru konumda gösterilir.
      </p>
    </div>
  );
};

const DRAFT_KEY = "restaurantApplyDraft";

const loadDraft = (): { formData: FormData; step: number } | null => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const saveDraft = (formData: FormData, step: number) => {
  localStorage.setItem(DRAFT_KEY, JSON.stringify({ formData, step }));
};

const clearDraft = () => {
  localStorage.removeItem(DRAFT_KEY);
};

const RestaurantApplyPage = () => {
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData & { photos: string }>>({});
  const [uploadedPhotos, setUploadedPhotos] = useState<string[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);

  const draft = loadDraft();
  const [step, setStep] = useState(draft ? draft.step : 1);
  const [formData, setFormData] = useState<FormData>(
    draft
      ? draft.formData
      : {
          restaurantName: "",
          ownerName: "",
          email: "",
          phone: "",
          city: "",
          district: "",
          address: "",
          cuisine: "",
          priceRange: "",
          shortDesc: "",
          description: "",
          mustTry: "",
          website: "",
          instagram: "",
          openingHours: "",
          lat: "",
          lng: "",
        }
  );

  // Show draft banner once on mount if draft exists
  useEffect(() => {
    if (draft && !draftRestored) {
      setShowDraftBanner(true);
      setDraftRestored(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save draft on every change
  useEffect(() => {
    if (!submitted) {
      saveDraft(formData, step);
    }
  }, [formData, step, submitted]);

  const update = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleLocationSelect = useCallback((lat: string, lng: string, _address: string) => {
    setFormData((prev) => ({ ...prev, lat, lng }));
  }, []);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = MAX_PHOTOS - uploadedPhotos.length;
    if (remaining <= 0) {
      setErrors((prev) => ({ ...prev, photos: `En fazla ${MAX_PHOTOS} fotoğraf yükleyebilirsiniz.` }));
      return;
    }

    const toProcess = files.slice(0, remaining);
    setUploadingPhoto(true);
    setErrors((prev) => ({ ...prev, photos: "" }));

    let processed = 0;
    const newPhotos: string[] = [];

    toProcess.forEach((file) => {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, photos: `Her fotoğraf en fazla ${MAX_FILE_SIZE_MB}MB olabilir.` }));
        processed++;
        if (processed === toProcess.length) setUploadingPhoto(false);
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (ev.target?.result) {
          newPhotos.push(ev.target.result as string);
        }
        processed++;
        if (processed === toProcess.length) {
          setUploadedPhotos((prev) => [...prev, ...newPhotos]);
          setUploadingPhoto(false);
        }
      };
      reader.readAsDataURL(file);
    });

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePhoto = (index: number) => {
    setUploadedPhotos((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => ({ ...prev, photos: "" }));
  };

  const validateStep1 = () => {
    const e: Partial<FormData> = {};
    if (!formData.restaurantName.trim()) e.restaurantName = "Restoran adı zorunludur";
    if (!formData.ownerName.trim()) e.ownerName = "İşletme sahibi adı zorunludur";
    if (!formData.email.trim()) e.email = "E-posta zorunludur";
    if (!formData.phone.trim()) e.phone = "Telefon zorunludur";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e: Partial<FormData> = {};
    if (!formData.city) e.city = "Şehir seçiniz";
    if (!formData.district.trim()) e.district = "İlçe zorunludur";
    if (!formData.address.trim()) e.address = "Adres zorunludur";
    if (!formData.cuisine) e.cuisine = "Mutfak türü seçiniz";
    if (!formData.priceRange) e.priceRange = "Fiyat aralığı seçiniz";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep3 = () => {
    const e: Partial<FormData & { photos: string }> = {};
    if (!formData.shortDesc.trim()) e.shortDesc = "Kısa açıklama zorunludur";
    if (!formData.description.trim()) e.description = "Detaylı açıklama zorunludur";
    if (formData.description.length > 500) e.description = "En fazla 500 karakter";
    if (!formData.mustTry.trim()) e.mustTry = "En az bir lezzet giriniz";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
    else if (step === 3 && validateStep3()) setStep(4);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep3()) return;
    setSubmitting(true);

    const coverImage = uploadedPhotos.length > 0
      ? uploadedPhotos[0]
      : `https://readdy.ai/api/search-image?query=Turkish%20restaurant%20$%7BformData.cuisine%7D%20food%20authentic%20interior%20warm%20atmosphere&width=800&height=500&seq=apply-${Date.now()}&orientation=landscape`;

    const pending = JSON.parse(localStorage.getItem("pendingRestaurants") || "[]");
    const newEntry = {
      id: `apply-${Date.now()}`,
      ...formData,
      mustTry: formData.mustTry.split(",").map((s) => s.trim()).filter(Boolean),
      status: "pending",
      appliedAt: new Date().toISOString(),
      rating: 0,
      image: coverImage,
      images: uploadedPhotos.length > 0 ? uploadedPhotos : [],
      tags: [formData.cuisine, formData.city],
      featured: false,
    };
    pending.push(newEntry);
    localStorage.setItem("pendingRestaurants", JSON.stringify(pending));
    clearDraft();

    // Admin bildirim e-postası
    const adminEmail = localStorage.getItem("adminNotifEmail") || "admin@geziturkiyem.com";
    const adminSubject = `Yeni Restoran Başvurusu: ${formData.restaurantName}`;
    const adminBody = `Yeni bir restoran başvurusu alındı!\n\nRestoran: ${formData.restaurantName}\nŞehir: ${formData.city}, ${formData.district}\nMutfak: ${formData.cuisine}\nSahibi: ${formData.ownerName}\nE-posta: ${formData.email}\nTelefon: ${formData.phone}\n\nAdmin panelinden inceleyebilirsiniz:\nhttps://geziturkiyem.com/admin`;
    window.open(`mailto:${adminEmail}?subject=${encodeURIComponent(adminSubject)}&body=${encodeURIComponent(adminBody)}`, "_blank");

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      body.append("photoCount", String(uploadedPhotos.length));
      await fetch("https://readdy.ai/api/form/d7cll1mm3fsfopf23fo0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } catch {
      // silent
    }

    setSubmitting(false);
    setSubmitted(true);
  };

  const steps = [
    { num: 1, label: "İletişim" },
    { num: 2, label: "Konum & Tür" },
    { num: 3, label: "Detaylar" },
    { num: 4, label: "Önizleme" },
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center px-4 pt-20">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-emerald-50 rounded-full">
              <i className="ri-checkbox-circle-line text-emerald-500 text-4xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-stone-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Başvurunuz Alındı!
            </h2>
            <p className="text-stone-500 text-sm leading-relaxed mb-2">
              <strong>{formData.restaurantName}</strong> için başvurunuz editörlerimize iletildi.
            </p>
            {uploadedPhotos.length > 0 && (
              <p className="text-stone-400 text-sm mb-2">
                <strong>{uploadedPhotos.length} fotoğraf</strong> başarıyla yüklendi.
              </p>
            )}
            {formData.lat && formData.lng && (
              <p className="text-stone-400 text-sm mb-2">
                <i className="ri-map-pin-line text-amber-500 mr-1"></i>
                Konum bilgisi kaydedildi.
              </p>
            )}
            <p className="text-stone-400 text-sm mb-8">
              İnceleme süreci genellikle 2-5 iş günü sürmektedir. Onay durumunu <strong>{formData.email}</strong> adresinize bildireceğiz.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate("/restoranlar")}
                className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Restoranları Keşfet
              </button>
              <button
                onClick={() => {
                  clearDraft();
                  setSubmitted(false);
                  setStep(1);
                  setUploadedPhotos([]);
                  setFormData({ restaurantName: "", ownerName: "", email: "", phone: "", city: "", district: "", address: "", cuisine: "", priceRange: "", shortDesc: "", description: "", mustTry: "", website: "", instagram: "", openingHours: "", lat: "", lng: "" });
                }}
                className="px-6 py-2.5 border border-stone-200 text-stone-600 rounded-full text-sm font-medium hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Yeni Başvuru
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 pb-12 bg-stone-900 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Turkish%20restaurant%20kitchen%20chef%20cooking%20traditional%20food%20warm%20atmosphere%20professional%20culinary&width=1400&height=350&seq=apply-header&orientation=landscape"
          alt="Restoran Başvurusu"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-25"
        />
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <span className="text-amber-400 text-sm font-semibold uppercase tracking-widest">İşletme Sahipleri İçin</span>
          <h1 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Restoranınızı Ekleyin
          </h1>
          <p className="text-stone-300 text-sm max-w-lg mx-auto">
            Türkiye'nin en kapsamlı seyahat rehberine restoranınızı ekleyin. Editörlerimiz başvurunuzu inceleyip onaylayacak.
          </p>
        </div>
      </section>

      {/* Step Indicator */}
      <div className="bg-white border-b border-stone-100 py-4">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-center gap-0">
            {steps.map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all
                    ${step > s.num ? "bg-emerald-500 text-white" : step === s.num ? "bg-amber-500 text-white" : "bg-stone-200 text-stone-400"}`}>
                    {step > s.num ? <i className="ri-check-line"></i> : s.num}
                  </div>
                  <span className={`text-xs mt-1 whitespace-nowrap ${step === s.num ? "text-amber-600 font-medium" : "text-stone-400"}`}>
                    {s.label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`w-16 md:w-24 h-0.5 mb-4 mx-1 transition-all ${step > s.num ? "bg-emerald-400" : "bg-stone-200"}`}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Draft Restored Banner */}
        {showDraftBanner && (
          <div className="mb-5 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
              <i className="ri-save-3-line text-amber-600 text-sm"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-800">Kaldığınız yerden devam ediyorsunuz</p>
              <p className="text-xs text-amber-600 mt-0.5">
                Önceki oturumunuzdan kaydedilmiş bir taslak bulundu.
                {draft && draft.step > 1 && ` Adım ${draft.step}'den devam ediyorsunuz.`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  clearDraft();
                  setStep(1);
                  setFormData({ restaurantName: "", ownerName: "", email: "", phone: "", city: "", district: "", address: "", cuisine: "", priceRange: "", shortDesc: "", description: "", mustTry: "", website: "", instagram: "", openingHours: "", lat: "", lng: "" });
                  setShowDraftBanner(false);
                }}
                className="text-xs text-amber-600 hover:text-amber-800 underline cursor-pointer whitespace-nowrap"
              >
                Sıfırla
              </button>
              <button
                type="button"
                onClick={() => setShowDraftBanner(false)}
                className="w-6 h-6 flex items-center justify-center text-amber-500 hover:text-amber-700 cursor-pointer"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          </div>
        )}

        {/* Auto-save indicator */}
        <div className="flex items-center justify-end gap-1.5 mb-3">
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-save-line text-stone-300 text-xs"></i>
          </div>
          <span className="text-xs text-stone-300">Taslak otomatik kaydediliyor</span>
        </div>

        <form ref={formRef} data-readdy-form onSubmit={handleSubmit}>
          {/* Step 1: Contact */}
          {step === 1 && (
            <div className="bg-white rounded-2xl border border-stone-100 p-6 md:p-8">
              <h2 className="text-xl font-bold text-stone-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                İletişim Bilgileri
              </h2>
              <p className="text-stone-400 text-sm mb-6">Başvuruyu yapan kişinin bilgileri</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Restoran Adı *</label>
                  <input
                    name="restaurantName"
                    type="text"
                    value={formData.restaurantName}
                    onChange={(e) => update("restaurantName", e.target.value)}
                    placeholder="Örn: Çiya Sofrası"
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors
                      ${errors.restaurantName ? "border-rose-400 bg-rose-50" : "border-stone-200 focus:border-amber-400"}`}
                  />
                  {errors.restaurantName && <p className="text-rose-500 text-xs mt-1">{errors.restaurantName}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">İşletme Sahibi / Yetkili *</label>
                  <input
                    name="ownerName"
                    type="text"
                    value={formData.ownerName}
                    onChange={(e) => update("ownerName", e.target.value)}
                    placeholder="Ad Soyad"
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors
                      ${errors.ownerName ? "border-rose-400 bg-rose-50" : "border-stone-200 focus:border-amber-400"}`}
                  />
                  {errors.ownerName && <p className="text-rose-500 text-xs mt-1">{errors.ownerName}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">E-posta *</label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="ornek@email.com"
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors
                      ${errors.email ? "border-rose-400 bg-rose-50" : "border-stone-200 focus:border-amber-400"}`}
                  />
                  {errors.email && <p className="text-rose-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Telefon *</label>
                  <input
                    name="phone"
                    type="text"
                    value={formData.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+90 5XX XXX XX XX"
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors
                      ${errors.phone ? "border-rose-400 bg-rose-50" : "border-stone-200 focus:border-amber-400"}`}
                  />
                  {errors.phone && <p className="text-rose-500 text-xs mt-1">{errors.phone}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Location & Type */}
          {step === 2 && (
            <div className="bg-white rounded-2xl border border-stone-100 p-6 md:p-8">
              <h2 className="text-xl font-bold text-stone-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Konum & Restoran Türü
              </h2>
              <p className="text-stone-400 text-sm mb-6">Restoranınızın nerede olduğunu ve ne tür yemekler sunduğunu belirtin</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Şehir *</label>
                  <select
                    name="city"
                    value={formData.city}
                    onChange={(e) => update("city", e.target.value)}
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors cursor-pointer
                      ${errors.city ? "border-rose-400 bg-rose-50" : "border-stone-200 focus:border-amber-400"}`}
                  >
                    <option value="">Şehir seçin</option>
                    {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {errors.city && <p className="text-rose-500 text-xs mt-1">{errors.city}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">İlçe / Semt *</label>
                  <input
                    name="district"
                    type="text"
                    value={formData.district}
                    onChange={(e) => update("district", e.target.value)}
                    placeholder="Örn: Kadıköy, Beyoğlu"
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors
                      ${errors.district ? "border-rose-400 bg-rose-50" : "border-stone-200 focus:border-amber-400"}`}
                  />
                  {errors.district && <p className="text-rose-500 text-xs mt-1">{errors.district}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Açık Adres *</label>
                  <input
                    name="address"
                    type="text"
                    value={formData.address}
                    onChange={(e) => update("address", e.target.value)}
                    placeholder="Sokak, mahalle, bina no..."
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors
                      ${errors.address ? "border-rose-400 bg-rose-50" : "border-stone-200 focus:border-amber-400"}`}
                  />
                  {errors.address && <p className="text-rose-500 text-xs mt-1">{errors.address}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Mutfak Türü *</label>
                  <select
                    name="cuisine"
                    value={formData.cuisine}
                    onChange={(e) => update("cuisine", e.target.value)}
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors cursor-pointer
                      ${errors.cuisine ? "border-rose-400 bg-rose-50" : "border-stone-200 focus:border-amber-400"}`}
                  >
                    <option value="">Mutfak türü seçin</option>
                    {cuisineOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {errors.cuisine && <p className="text-rose-500 text-xs mt-1">{errors.cuisine}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Fiyat Aralığı *</label>
                  <div className="flex gap-2">
                    {priceOptions.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => update("priceRange", p)}
                        className={`flex-1 py-2.5 rounded-lg border text-sm font-bold transition-all cursor-pointer
                          ${formData.priceRange === p ? "border-amber-500 bg-amber-50 text-amber-700" : "border-stone-200 text-stone-500 hover:border-stone-300"}`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                  {errors.priceRange && <p className="text-rose-500 text-xs mt-1">{errors.priceRange}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Çalışma Saatleri</label>
                  <input
                    name="openingHours"
                    type="text"
                    value={formData.openingHours}
                    onChange={(e) => update("openingHours", e.target.value)}
                    placeholder="Örn: 09:00 - 22:00 (Her gün)"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Website</label>
                  <input
                    name="website"
                    type="text"
                    value={formData.website}
                    onChange={(e) => update("website", e.target.value)}
                    placeholder="https://restoraniniz.com"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Instagram</label>
                  <input
                    name="instagram"
                    type="text"
                    value={formData.instagram}
                    onChange={(e) => update("instagram", e.target.value)}
                    placeholder="@restoraniniz"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none focus:border-amber-400 transition-colors"
                  />
                </div>

                {/* Google Maps Location Picker */}
                <div className="sm:col-span-2">
                  <div className="flex items-center gap-2 mb-3">
                    <label className="block text-xs font-semibold text-stone-600">
                      Haritadan Konum Seç
                    </label>
                    <span className="text-xs text-stone-400 font-normal">(İsteğe bağlı — harita görünümünde doğru konumda gösterilir)</span>
                    {formData.lat && formData.lng && (
                      <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                        <i className="ri-checkbox-circle-fill"></i> Seçildi
                      </span>
                    )}
                  </div>
                  <MapLocationPicker
                    lat={formData.lat}
                    lng={formData.lng}
                    city={formData.city}
                    onLocationSelect={handleLocationSelect}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Details + Photos */}
          {step === 3 && (
            <div className="bg-white rounded-2xl border border-stone-100 p-6 md:p-8">
              <h2 className="text-xl font-bold text-stone-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Restoran Detayları
              </h2>
              <p className="text-stone-400 text-sm mb-6">Ziyaretçilere restoranınızı tanıtın</p>
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Kısa Açıklama * <span className="text-stone-400 font-normal">(1-2 cümle, liste kartında görünür)</span></label>
                  <input
                    name="shortDesc"
                    type="text"
                    value={formData.shortDesc}
                    onChange={(e) => update("shortDesc", e.target.value)}
                    placeholder="Restoranınızı en iyi anlatan kısa bir cümle..."
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors
                      ${errors.shortDesc ? "border-rose-400 bg-rose-50" : "border-stone-200 focus:border-amber-400"}`}
                  />
                  {errors.shortDesc && <p className="text-rose-500 text-xs mt-1">{errors.shortDesc}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                    Detaylı Açıklama * <span className="text-stone-400 font-normal">({formData.description.length}/500 karakter)</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) update("description", e.target.value);
                    }}
                    rows={5}
                    placeholder="Restoranınızın hikayesi, özelliği, atmosferi, menüsü hakkında detaylı bilgi verin..."
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors resize-none
                      ${errors.description ? "border-rose-400 bg-rose-50" : "border-stone-200 focus:border-amber-400"}`}
                  />
                  {errors.description && <p className="text-rose-500 text-xs mt-1">{errors.description}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                    Mutlaka Denenecek Lezzetler * <span className="text-stone-400 font-normal">(virgülle ayırın)</span>
                  </label>
                  <input
                    name="mustTry"
                    type="text"
                    value={formData.mustTry}
                    onChange={(e) => update("mustTry", e.target.value)}
                    placeholder="Örn: Fıstıklı Baklava, Katmer, Beyran Çorbası"
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors
                      ${errors.mustTry ? "border-rose-400 bg-rose-50" : "border-stone-200 focus:border-amber-400"}`}
                  />
                  {errors.mustTry && <p className="text-rose-500 text-xs mt-1">{errors.mustTry}</p>}
                  {formData.mustTry && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {formData.mustTry.split(",").map((item, i) => item.trim() && (
                        <span key={i} className="bg-amber-50 text-amber-700 text-xs px-2 py-0.5 rounded-full">{item.trim()}</span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Photo Upload Section */}
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                    Restoran Fotoğrafları
                    <span className="text-stone-400 font-normal ml-1">(İsteğe bağlı, en fazla {MAX_PHOTOS} fotoğraf, her biri max {MAX_FILE_SIZE_MB}MB)</span>
                  </label>

                  {uploadedPhotos.length < MAX_PHOTOS && (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-stone-200 rounded-xl p-6 text-center cursor-pointer hover:border-amber-400 hover:bg-amber-50/30 transition-all group"
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                      {uploadingPhoto ? (
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-10 h-10 flex items-center justify-center">
                            <i className="ri-loader-4-line text-amber-500 text-2xl animate-spin"></i>
                          </div>
                          <p className="text-sm text-stone-500">Fotoğraflar yükleniyor...</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-12 h-12 flex items-center justify-center bg-stone-100 group-hover:bg-amber-100 rounded-xl transition-colors">
                            <i className="ri-image-add-line text-stone-400 group-hover:text-amber-500 text-2xl transition-colors"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-stone-700 group-hover:text-amber-700 transition-colors">
                              Fotoğraf Yükle
                            </p>
                            <p className="text-xs text-stone-400 mt-0.5">
                              JPG, PNG veya WebP · {MAX_PHOTOS - uploadedPhotos.length} fotoğraf daha ekleyebilirsiniz
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {errors.photos && <p className="text-rose-500 text-xs mt-1">{errors.photos}</p>}

                  {uploadedPhotos.length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-stone-500">{uploadedPhotos.length} fotoğraf yüklendi</p>
                        {uploadedPhotos.length > 0 && (
                          <span className="text-xs text-amber-600 font-medium">
                            <i className="ri-star-fill text-xs mr-1"></i>İlk fotoğraf kapak görseli olacak
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                        {uploadedPhotos.map((photo, index) => (
                          <div key={index} className="relative group">
                            <div className="w-full aspect-square rounded-lg overflow-hidden border border-stone-200">
                              <img
                                src={photo}
                                alt={`Fotoğraf ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            {index === 0 && (
                              <div className="absolute top-1 left-1 bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-md font-medium leading-none">
                                Kapak
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => removePhoto(index)}
                              className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-rose-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            >
                              <i className="ri-close-line text-xs"></i>
                            </button>
                          </div>
                        ))}
                        {uploadedPhotos.length < MAX_PHOTOS && (
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="w-full aspect-square rounded-lg border-2 border-dashed border-stone-200 flex items-center justify-center hover:border-amber-400 hover:bg-amber-50/30 transition-all cursor-pointer"
                          >
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-add-line text-stone-400 text-lg"></i>
                            </div>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Preview */}
          {step === 4 && (
            <div className="space-y-5">
              <div className="bg-white rounded-2xl border border-stone-100 p-6 md:p-8">
                <h2 className="text-xl font-bold text-stone-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Başvuru Önizlemesi
                </h2>
                <p className="text-stone-400 text-sm mb-6">Bilgilerinizi kontrol edin ve gönderin</p>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-xs text-stone-400 mb-1">Restoran Adı</p>
                      <p className="font-semibold text-stone-900 text-sm">{formData.restaurantName}</p>
                    </div>
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-xs text-stone-400 mb-1">İşletme Sahibi</p>
                      <p className="font-semibold text-stone-900 text-sm">{formData.ownerName}</p>
                    </div>
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-xs text-stone-400 mb-1">E-posta</p>
                      <p className="font-semibold text-stone-900 text-sm">{formData.email}</p>
                    </div>
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-xs text-stone-400 mb-1">Telefon</p>
                      <p className="font-semibold text-stone-900 text-sm">{formData.phone}</p>
                    </div>
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-xs text-stone-400 mb-1">Konum</p>
                      <p className="font-semibold text-stone-900 text-sm">{formData.city}, {formData.district}</p>
                    </div>
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-xs text-stone-400 mb-1">Mutfak & Fiyat</p>
                      <p className="font-semibold text-stone-900 text-sm">{formData.cuisine} · {formData.priceRange}</p>
                    </div>
                  </div>

                  {/* Location preview */}
                  {formData.lat && formData.lng && (
                    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-map-pin-2-fill text-emerald-600 text-sm"></i>
                        </div>
                        <p className="text-xs font-semibold text-emerald-800">Harita Konumu Seçildi</p>
                      </div>
                      <p className="text-xs text-emerald-600">
                        Enlem: {parseFloat(formData.lat).toFixed(5)} · Boylam: {parseFloat(formData.lng).toFixed(5)}
                      </p>
                      <p className="text-xs text-emerald-500 mt-1">Restoranınız harita görünümünde doğru konumda gösterilecek.</p>
                    </div>
                  )}

                  <div className="bg-stone-50 rounded-xl p-4">
                    <p className="text-xs text-stone-400 mb-1">Kısa Açıklama</p>
                    <p className="text-stone-700 text-sm">{formData.shortDesc}</p>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-4">
                    <p className="text-xs text-stone-400 mb-2">Mutlaka Denenecekler</p>
                    <div className="flex flex-wrap gap-1.5">
                      {formData.mustTry.split(",").map((item, i) => item.trim() && (
                        <span key={i} className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">{item.trim()}</span>
                      ))}
                    </div>
                  </div>

                  {uploadedPhotos.length > 0 && (
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-xs text-stone-400 mb-2">Yüklenen Fotoğraflar ({uploadedPhotos.length})</p>
                      <div className="flex gap-2 flex-wrap">
                        {uploadedPhotos.map((photo, i) => (
                          <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-stone-200">
                            <img src={photo} alt={`Fotoğraf ${i + 1}`} className="w-full h-full object-cover" />
                            {i === 0 && (
                              <div className="absolute bottom-0 left-0 right-0 bg-amber-500/80 text-white text-center text-xs py-0.5">
                                Kapak
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-100">
                  <div className="flex gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-information-line text-amber-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-amber-800 text-xs font-semibold mb-1">İnceleme Süreci</p>
                      <p className="text-amber-700 text-xs leading-relaxed">
                        Başvurunuz editörlerimiz tarafından 2-5 iş günü içinde incelenecektir. Onaylanması durumunda restoranınız sitemizde yayınlanacak ve size e-posta ile bildirim gönderilecektir.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-6">
            {step > 1 ? (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="flex items-center gap-2 px-5 py-2.5 border border-stone-200 text-stone-600 rounded-full text-sm font-medium hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> Geri
              </button>
            ) : (
              <div className="flex items-center gap-1.5 text-xs text-stone-300">
                <i className="ri-shield-check-line text-xs"></i>
                <span>Verileriniz güvende</span>
              </div>
            )}

            {step < 4 ? (
              <button
                type="button"
                onClick={handleNext}
                className="flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Devam Et <i className="ri-arrow-right-line"></i>
              </button>
            ) : (
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 px-6 py-2.5 bg-emerald-500 text-white rounded-full text-sm font-medium hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {submitting ? (
                  <><i className="ri-loader-4-line animate-spin"></i> Gönderiliyor...</>
                ) : (
                  <><i className="ri-send-plane-line"></i> Başvuruyu Gönder</>
                )}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Application Status Checker */}
      <div className="max-w-3xl mx-auto px-4 pb-12">
        <div className="relative flex items-center gap-4 my-2">
          <div className="flex-1 h-px bg-stone-200"></div>
          <span className="text-xs text-stone-400 whitespace-nowrap font-medium">veya</span>
          <div className="flex-1 h-px bg-stone-200"></div>
        </div>
        <ApplicationStatusChecker />
      </div>

      <Footer />
    </div>
  );
};

export default RestaurantApplyPage;
